/**
 * Copyright IBM Corp. 2022, 2026
 */

import {
	Table as MDSTable,
	getDensity,
	getHorizontalAlignment,
	getScope,
	getVerticalAlignment,
	Button,
	FlightIcon,
	InlineLink,
} from '@hashicorp/mds-react/components'
import {
	hasHashiCorpDestination,
	parseUrl,
} from '@hashicorp/react-components/src/lib/parse-url'
import parse, { domToReact, attributesToProps } from 'html-react-parser'
import classNames from 'classnames'
import type {
	DOMNode,
	Element,
	HTMLReactParserOptions,
	Text,
} from 'html-react-parser'
import s from './style.module.css'

interface TableProps {
	/**
	 * HTML string that will be parsed into a table
	 */
	table: string
}

export default function Table({ table }: TableProps) {
	// Used to determine role attribute in <table> element for accessibility
	// Also applies styles to the first row if the table has no header
	const tableHasNoHeader = !table.includes('</thead>')

	return (
		<>
			{parse(table, {
				replace: (domNode: DOMNode) => {
					if (
						'name' in domNode &&
						domNode.name === 'table' &&
						domNode.type !== 'directive'
					) {
						const props = attributesToProps(domNode.attribs)

						return (
							<MDSTable.Root
								role={tableHasNoHeader ? 'presentation' : undefined}
								// Default to true unless explicitly set to false
								isStriped={props['is-striped'] !== 'false'}
								className={classNames(s.table, {
									[s.noHeader]: tableHasNoHeader,
								})}
								isFixedLayout={props.style?.tableLayout === 'fixed'}
								valign={
									props.style?.verticalAlign
										? getVerticalAlignment(props.style.verticalAlign)
										: undefined
								}
								density={
									props.style?.density
										? getDensity(props.style.density)
										: undefined
								}
							>
								{domToReact(domNode.children, options)}
							</MDSTable.Root>
						)
					}
				},
			})}
		</>
	)
}

const options: HTMLReactParserOptions = {
	replace: ({ name, children, attribs }: Element) => {
		const props = attributesToProps(attribs)

		switch (name) {
			case 'thead':
				return <MDSTable.THead>{domToReact(children, options)}</MDSTable.THead>
			case 'tr':
				return (
					<MDSTable.TR {...props}>{domToReact(children, options)}</MDSTable.TR>
				)
			case 'th': {
				return (
					<MDSTable.TH
						{...props}
						className={s.tableHeader}
						scope={props.scope ? getScope(props.scope) : undefined}
						align={
							props.style?.textAlign
								? getHorizontalAlignment(props.style.textAlign)
								: undefined
						}
						style={{
							['--col-width' as string]: props.width || props.style?.width,
						}}
						isVisuallyHidden={Boolean(props['is-visually-hidden'])}
						tooltip={props.tooltip}
					>
						{domToReact(children, cellOptions)}
					</MDSTable.TH>
				)
			}
			case 'tbody':
				return <MDSTable.TBody>{domToReact(children, options)}</MDSTable.TBody>
			case 'td': {
				return (
					<MDSTable.TD
						{...props}
						className={s.tableCell}
						align={
							props.style?.textAlign
								? getHorizontalAlignment(props.style.textAlign)
								: undefined
						}
					>
						<div className={s.cellContent}>
							{domToReact(children, cellOptions)}
						</div>
					</MDSTable.TD>
				)
			}
		}
	},
}

const cellOptions: HTMLReactParserOptions = {
	replace: (props: Element | Text) => {
		switch (props.type) {
			case 'text':
				return replaceText(props)
			case 'tag':
				return replaceTag(props)
		}
	},
}

function replaceTag({ name, children, attribs }: Element) {
	const props = attributesToProps(attribs)
	switch (name) {
		case 'a': {
			if (props.type === 'button' && children[0]?.type === 'text') {
				return <Button {...(props as any)} text={children[0].data} />
			}

			if (children[0]?.type === 'text') {
				const parsedUrl = parseUrl(attribs.href)
				return (
					<InlineLink
						href={parsedUrl.href}
						text={children[0].data}
						color="secondary"
						isHrefExternal={!hasHashiCorpDestination(parsedUrl.href)}
					/>
				)
			}
		}
	}
}

const iconMapping = {
	10004: {
		name: 'check-circle-fill',
		color: 'var(--mds-color-foreground-success)',
		title: 'Yes',
	},
	10008: {
		name: 'x-circle',
		color: 'var(--mds-color-foreground-faint)',
		title: 'No',
	},
}
/**
 * Replace the ✔ and ✘ characters with the FlightIcon component
 * These symbols are commonly used by our teams to indicate true/false values
 */
function replaceText({ data }: Text) {
	if (data?.length === 1 && iconMapping[data.charCodeAt(0)]) {
		const { name, color, title } = iconMapping[data.charCodeAt(0)]
		return (
			<div className={s.icon}>
				<FlightIcon
					name={name}
					size={24}
					color={color}
					title={title}
					isInlineBlock={false}
				/>
			</div>
		)
	}
}
